import React, { useMemo } from 'react';
import {
  format, addDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, isSameDay, isToday,
  parseISO, isWithinInterval, set,
} from 'date-fns';
import { MapPin, User, Clock, StickyNote } from 'lucide-react';

// Lightweight, mobile-friendly staff calendar.
// Read-only — purely a visualisation layer over /roster/my-shifts.
// Three views: day | week | month. Highlights today + currently-running shift.

function statusColour(status) {
  // Aligned with the finalised Scheduling Board colour system (Feb 2026):
  //   Open=Blue · Assigned=Green · In Progress=Purple · Completed=Grey
  //   Invoiced=Teal · Cancelled=Stone · No Show=Red
  if (status === 'IN_PROGRESS') return 'bg-violet-100 text-violet-800 border-violet-300';
  if (status === 'COMPLETED')   return 'bg-slate-100 text-slate-700 border-slate-300';
  if (status === 'INVOICED')    return 'bg-teal-100 text-teal-800 border-teal-300';
  if (status === 'NO_SHOW')     return 'bg-red-100 text-red-800 border-red-300';
  if (status === 'CANCELLED')   return 'bg-stone-100 text-stone-600 border-stone-300';
  if (status === 'OPEN')        return 'bg-blue-50 text-blue-800 border-blue-200';
  // Default: ASSIGNED / CONFIRMED → Green
  return 'bg-emerald-50 text-emerald-800 border-emerald-200';
}

function timeRange(s) {
  const st = s.start_time || '';
  const et = s.end_time || '';
  return st + '–' + et;
}

function shiftLocation(s) {
  if (s.address) return s.address;   // client#7: house / participant address for the worker's own shifts
  if (s.location) return s.location;
  if (s.house_name) return s.house_name;
  return '';
}

function isCurrentlyRunning(s, now) {
  if (s.status !== 'ASSIGNED' && s.status !== 'IN_PROGRESS' && s.status !== 'CONFIRMED') return false;
  if (!s.date || !s.start_time || !s.end_time) return false;
  try {
    const day = parseISO(s.date);
    const startParts = s.start_time.split(':').map(Number);
    const endParts = s.end_time.split(':').map(Number);
    const start = set(day, { hours: startParts[0], minutes: startParts[1] || 0, seconds: 0, milliseconds: 0 });
    let end = set(day, { hours: endParts[0], minutes: endParts[1] || 0, seconds: 0, milliseconds: 0 });
    if (end <= start) end = addDays(end, 1); // crosses midnight
    return isWithinInterval(now, { start, end });
  } catch {
    return false;
  }
}

function ShiftCard(props) {
  const s = props.shift;
  const onClick = props.onClick;
  const compact = props.compact;
  const running = props.running;
  const cls = statusColour(s.status);
  const ringCls = running ? 'ring-2 ring-sky-500 ring-offset-1' : '';
  const handleClick = () => { if (onClick) onClick(s); };
  const loc = shiftLocation(s);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={'block w-full text-left rounded-lg border p-2 transition hover:shadow-sm ' + cls + ' ' + ringCls}
      data-testid={'staff-cal-shift-' + s.id}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold tabular-nums flex items-center gap-1">
          <Clock className="w-3 h-3 opacity-70" />
          {timeRange(s)}
        </span>
      </div>
      {!compact && (
        <div className="mt-1 space-y-0.5">
          {s.participant_name && (
            <div className="flex items-center gap-1 text-xs font-medium truncate">
              <User className="w-3 h-3 opacity-70 shrink-0" />
              <span className="truncate">{s.participant_name}</span>
            </div>
          )}
          {loc && (
            <div className="flex items-center gap-1 text-[11px] opacity-80 truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{loc}</span>
            </div>
          )}
          {s.notes && (
            <div className="flex items-start gap-1 text-[11px] opacity-90" title={s.notes} data-testid={`roster-cal-notes-${s.id}`}>
              <StickyNote className="w-3 h-3 shrink-0 mt-0.5" />
              <span className="line-clamp-2">{s.notes}</span>
            </div>
          )}
        </div>
      )}
      {compact && s.participant_name && (
        <div className="mt-0.5 text-[10px] font-medium truncate" title={s.participant_name}>
          {s.participant_name}
        </div>
      )}
      {compact && s.notes && (
        <div className="text-[10px] opacity-80 italic truncate" title={s.notes}>
          {s.notes}
        </div>
      )}
    </button>
  );
}


function DayView(props) {
  const date = props.date;
  const shifts = props.shifts;
  const onShiftClick = props.onShiftClick;
  const now = props.now;
  const dk = format(date, 'yyyy-MM-dd');
  const dayShifts = shifts.filter(s => s.date === dk).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const todayCls = isToday(date) ? 'bg-blue-50 border-blue-300' : 'bg-white';
  return (
    <div className="space-y-3">
      <div className={'rounded-xl border p-3 ' + todayCls}>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{format(date, 'EEEE')}</div>
        <div className="text-2xl font-bold tabular-nums">{format(date, 'd MMMM yyyy')}</div>
      </div>
      {dayShifts.length === 0 ? (
        <div className="rounded-xl border bg-white p-6 text-center text-sm text-muted-foreground">
          No shifts today.
        </div>
      ) : (
        <div className="space-y-2">
          {dayShifts.map(s => (
            <ShiftCard key={s.id} shift={s} onClick={onShiftClick} running={isCurrentlyRunning(s, now)} />
          ))}
        </div>
      )}
    </div>
  );
}


function WeekView(props) {
  const start = props.weekStart;
  const shifts = props.shifts;
  const onShiftClick = props.onShiftClick;
  const now = props.now;
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i = i + 1) arr.push(addDays(start, i));
    return arr;
  }, [start]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {days.map(d => {
        const dk = format(d, 'yyyy-MM-dd');
        const list = shifts.filter(s => s.date === dk).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
        const today = isToday(d);
        const headCls = today ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700';
        const cellCls = today ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-slate-200';
        return (
          <div key={dk} className={'rounded-xl border ' + cellCls}>
            <div className={'rounded-t-xl px-2 py-1.5 ' + headCls}>
              <div className="text-[10px] uppercase tracking-wider opacity-90">{format(d, 'EEE')}</div>
              <div className="text-base font-bold tabular-nums leading-tight">{format(d, 'd MMM')}</div>
            </div>
            <div className="p-1.5 space-y-1.5 min-h-[110px]">
              {list.length === 0 ? (
                <div className="text-[11px] text-muted-foreground text-center pt-3">—</div>
              ) : (
                list.map(s => (
                  <ShiftCard key={s.id} shift={s} onClick={onShiftClick} compact running={isCurrentlyRunning(s, now)} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function MonthView(props) {
  const date = props.date;
  const shifts = props.shifts;
  const onShiftClick = props.onShiftClick;
  const now = props.now;
  const grid = useMemo(() => {
    const ms = startOfMonth(date);
    const me = endOfMonth(date);
    const gridStart = startOfWeek(ms, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(me, { weekStartsOn: 1 });
    const days = [];
    let d = gridStart;
    while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }
    return { days, monthStart: ms, monthEnd: me };
  }, [date]);
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-muted-foreground text-center font-semibold">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.days.map(d => {
          const dk = format(d, 'yyyy-MM-dd');
          const list = shifts.filter(s => s.date === dk);
          const inMonth = d >= grid.monthStart && d <= grid.monthEnd;
          const today = isToday(d);
          const baseCls = today ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200';
          const dimCls = inMonth ? '' : 'opacity-40';
          return (
            <div key={dk} className={'rounded-md border p-1 min-h-[68px] ' + baseCls + ' ' + dimCls}>
              <div className={'text-[11px] font-semibold tabular-nums ' + (today ? 'text-blue-700' : 'text-slate-700')}>
                {format(d, 'd')}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {list.slice(0, 2).map(s => {
                  const running = isCurrentlyRunning(s, now);
                  const cls = statusColour(s.status);
                  const ringCls = running ? 'ring-1 ring-sky-500' : '';
                  const handle = () => { if (onShiftClick) onShiftClick(s); };
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={handle}
                      className={'w-full text-[9px] px-1 py-0.5 rounded truncate text-left border ' + cls + ' ' + ringCls}
                      data-testid={'staff-cal-month-shift-' + s.id}
                    >
                      {s.start_time} {s.participant_name || ''}
                    </button>
                  );
                })}
                {list.length > 2 && (
                  <div className="text-[9px] text-muted-foreground">+{list.length - 2} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function StaffRosterCalendar({ view, currentDate, shifts, onShiftClick }) {
  const now = useMemo(() => new Date(), [shifts]);
  if (view === 'day') {
    return <DayView date={currentDate} shifts={shifts} onShiftClick={onShiftClick} now={now} />;
  }
  if (view === 'month') {
    return <MonthView date={currentDate} shifts={shifts} onShiftClick={onShiftClick} now={now} />;
  }
  const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
  return <WeekView weekStart={ws} shifts={shifts} onShiftClick={onShiftClick} now={now} />;
}
